// =====================================================================
//  ADMIN CONSOLE, NAVIGATION MAP
// ---------------------------------------------------------------------
//  Single source of truth for the console's structure. The command rail,
//  the breadcrumb trail, the command palette and the router all read
//  from this list, so adding a screen is one entry here rather than four
//  edits that can fall out of sync.
//
//  Grouping is by *what the administrator is doing*, not by database
//  table, Workforce (the people), Talent (bringing people in),
//  Operations (running the service), Governance (who may do what).
// =====================================================================

import {
  Activity,
  BarChart3,
  Building2,
  CalendarDays,
  CheckSquare,
  Clock,
  FileCheck,
  FolderOpen,
  LayoutDashboard,
  LifeBuoy,
  Mail,
  ShieldCheck,
  SlidersHorizontal,
  Target,
  Timer,
  Users,
  Wallet,
} from 'lucide-react';

/**
 * `count` names a key on the approval-counts payload; the rail renders it
 * as an amber badge. `roles` restricts an item, omitted means every role
 * that can reach the console at all (admin, hr, manager).
 */
export const NAV_GROUPS = [
  {
    label: 'Analytics',
    items: [
      { key: 'overview', label: 'Overview', icon: LayoutDashboard, title: 'Overview' },
    ],
  },
  {
    label: 'Workforce',
    items: [
      { key: 'people', label: 'People', icon: Users, title: 'People' },
      { key: 'attendance', label: 'Attendance', icon: Clock, title: 'Attendance' },
      { key: 'leave', label: 'Leave', icon: CalendarDays, title: 'Leave', count: 'leave' },
      {
        key: 'approvals',
        label: 'Approvals',
        icon: CheckSquare,
        title: 'Approvals',
        count: 'approvals',
      },
      // Leave entitlements and the holiday calendar. Sits next to Leave
      // rather than under Governance because it is the thing an HR user
      // reaches for straight after looking at a leave queue.
      { key: 'leave-setup', label: 'Leave Setup', icon: SlidersHorizontal, title: 'Leave Setup' },
      { key: 'shifts', label: 'Shifts', icon: Timer, title: 'Shifts' },
      { key: 'payroll', label: 'Payroll', icon: Wallet, title: 'Payroll' },
      { key: 'performance', label: 'Performance', icon: Target, title: 'Performance' },
    ],
  },
  // Recruitment is deliberately absent.
  //
  // Hiring is a different job from running a workforce, with a different
  // audience (hiring managers and candidates, not HR operations) and a
  // different lifecycle, and it already has its own applications in this
  // repository. Carrying a second, thinner candidate pipeline inside the
  // HRMS console meant two places to look and two places to keep in step.
  //
  // `pages/RecruitmentPage.jsx` is still on disk but no longer reachable.
  {
    label: 'Onboarding',
    items: [
      { key: 'onboarding-invites', label: 'Invites', icon: Mail, title: 'Onboarding Invites' },
      { key: 'onboarding-submissions', label: 'Submissions', icon: FileCheck, title: 'Onboarding Submissions', count: 'onboardingSubmissions' },
      { key: 'onboarding-analytics', label: 'Analytics', icon: BarChart3, title: 'Onboarding Analytics' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { key: 'helpdesk', label: 'Helpdesk', icon: LifeBuoy, title: 'Helpdesk', count: 'openTickets' },
      { key: 'documents', label: 'Documents', icon: FolderOpen, title: 'Documents' },
      { key: 'reports', label: 'Reports', icon: BarChart3, title: 'Reports' },
    ],
  },
  {
    label: 'Governance',
    items: [
      {
        key: 'access',
        label: 'Access Control',
        icon: ShieldCheck,
        title: 'Access Control',
        count: 'governance',
        roles: ['admin', 'hr'],
      },
      { key: 'organization', label: 'Organization', icon: Building2, title: 'Organization' },
      { key: 'activity', label: 'Activity Log', icon: Activity, title: 'Activity Log' },
    ],
  },
];

/** Flat lookup of every nav item, keyed by route key. */
export const NAV_INDEX = NAV_GROUPS.reduce((map, group) => {
  group.items.forEach((item) => {
    map[item.key] = { ...item, group: group.label };
  });
  return map;
}, {});

export const DEFAULT_ROUTE = 'overview';

export const isValidRoute = (key) => Object.prototype.hasOwnProperty.call(NAV_INDEX, key);

/** Filters the groups a given role may see, dropping any that empty out. */
export const navGroupsForRole = (role) =>
  NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.roles || item.roles.includes(role)),
  })).filter((group) => group.items.length > 0);

/**
 * Resolves the badge number for a nav item. `governance` is a synthetic
 * bucket: access requests and account-removal requests are separate tables
 * but land on the same screen, so the rail shows their sum.
 */
export const countFor = (item, counts) => {
  if (!item.count || !counts) return 0;
  if (item.count === 'governance') {
    return (counts.accessRequests || 0) + (counts.deletionRequests || 0);
  }
  return counts[item.count] || 0;
};
